import type { PublicFormVersion } from "@kushya/shared";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";


const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const extractErrorMessage = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }

  const error = payload.error;

  if (typeof error === "string") {
    return error;
  }

  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }

  return undefined;
};

/**
 * Fetch the active published form version from the public API (unauthenticated).
 */
export async function fetchPublishedForm(
  projectId: string,
  formId: string,
): Promise<PublicFormVersion> {
  const encodedProjectId = encodeURIComponent(projectId);
  const encodedFormId = encodeURIComponent(formId);
  const url = `${API_BASE_URL}/forms/public/projects/${encodedProjectId}/forms/${encodedFormId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    let body: unknown;

    try {
      body = await response.json();
    } catch {
      body = undefined;
    }

    if (response.status === 404) {
      throw new Error("Form not found or has not been published");
    }

    if (response.status === 429) {
      throw new Error("Too many requests. Please try again later.");
    }

    const message = extractErrorMessage(body);
    throw new Error(message ?? "Failed to fetch form");
  }

  return (await response.json()) as PublicFormVersion;
}

/**
 * Fetch the active published form version using slugs (preferred for beautiful URLs).
 */
export async function fetchPublishedFormBySlug(
  projectSlug: string,
  formSlug: string,
): Promise<PublicFormVersion> {
  const encodedProjectSlug = encodeURIComponent(projectSlug);
  const encodedFormSlug = encodeURIComponent(formSlug);
  const url = `${API_BASE_URL}/forms/public/p/${encodedProjectSlug}/f/${encodedFormSlug}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    let body: unknown;

    try {
      body = await response.json();
    } catch {
      body = undefined;
    }

    if (response.status === 404) {
      throw new Error("Form not found or has not been published");
    }

    if (response.status === 429) {
      throw new Error("Too many requests. Please try again later.");
    }

    const message = extractErrorMessage(body);
    throw new Error(message ?? "Failed to fetch form");
  }

  return (await response.json()) as PublicFormVersion;
}
